import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import documentService from '../services/documentService';
import { USE_MOCK } from '../services/apiClient';
import { getExtension, ACCEPTED_EXTENSIONS } from '../utils/fileTypes';
import { formatBytes } from '../utils/format';

const UploadContext = createContext(null);

const MAX_SIZE_MB = 250;

/**
 * Owns the upload queue so uploads survive navigation (docs/01-frontend-architecture.md §6).
 *
 * Production path: presigned PUT via XHR (for progress), then a metadata commit that
 * references the upload session. When `documentId` is set the commit produces a NEW
 * version of that document rather than a new document — history is never overwritten.
 */
export function UploadProvider({ children }) {
  const [items, setItems] = useState([]);
  const [trayOpen, setTrayOpen] = useState(false);
  const [minimised, setMinimised] = useState(false);
  // The upload dialog shows this same queue while it is open. Both are modals now, so one
  // of them has to stand down rather than stack on top of the other.
  const [suppressed, setSuppressed] = useState(false);
  const timers = useRef({});
  const listeners = useRef(new Set());
  const idRef = useRef(0);

  const patch = useCallback((id, changes) => {
    setItems((list) => list.map((i) => (i.id === id ? { ...i, ...changes } : i)));
  }, []);

  const notifyComplete = useCallback((document, item) => {
    listeners.current.forEach((fn) => fn(document, item));
  }, []);

  const onUploadComplete = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  /**
   * Follow the server's pipeline until nothing is still running.
   *
   * This replaces a `setTimeout(..., 6200)` that flipped the row to "Complete" 6.2 seconds
   * after the commit, whether extraction had finished, failed, or never started. The
   * duration was invented; on a 40-page scan it lied, and on a failure it lied twice. Every
   * stage shown now comes from a status column the pipeline actually writes.
   */
  const followProcessing = useCallback(
    async (id, documentId) => {
      const started = Date.now();
      const LIMIT_MS = 3 * 60_000;

      while (Date.now() - started < LIMIT_MS) {
        let result;
        try {
          result = await documentService.processing(documentId);
        } catch {
          // The document is saved; only the progress report is unavailable. Stop reporting
          // rather than showing a stalled row forever.
          patch(id, { status: 'done' });
          return;
        }
        patch(id, { stages: result.stages || [] });
        if (result.settled) {
          patch(id, { status: 'done' });
          return;
        }
        await new Promise((resolve) => {
          timers.current[`${id}-poll`] = setTimeout(resolve, 1500);
        });
      }

      // Three minutes without settling means something is stuck on the server. Say so
      // rather than quietly showing a tick.
      patch(id, { status: 'done', stalled: true });
    },
    [patch],
  );

  const runUpload = useCallback(
    async (item) => {
      patch(item.id, { status: 'uploading', progress: 0, error: null, stages: [], checked: false });

      const finish = async (uploadId) => {
        patch(item.id, { status: 'committing', progress: 100 });
        try {
          const doc = await documentService.commitUpload(
            USE_MOCK
              ? {
                  fileName: item.name,
                  sizeBytes: item.size,
                  folderId: item.folderId,
                  mimeType: item.mimeType,
                  documentId: item.documentId || null,
                  changeNote: item.changeNote || '',
                }
              : { uploadId, changeNote: item.changeNote || '' },
          );
          patch(item.id, { status: 'processing', document: doc, committed: true });
          notifyComplete(doc, item);
          if (USE_MOCK) patch(item.id, { status: 'done' });
          else followProcessing(item.id, doc.id);
        } catch (error) {
          patch(item.id, { status: 'failed', error: error.message || 'Upload failed.' });
        }
      };

      if (USE_MOCK) {
        // Simulate a network upload with realistic, slightly uneven progress.
        const step = () => {
          setItems((list) => {
            const current = list.find((i) => i.id === item.id);
            if (!current || current.status !== 'uploading') return list;
            const next = Math.min(99, current.progress + 6 + Math.random() * 16);
            if (next >= 99) {
              clearInterval(timers.current[item.id]);
              setTimeout(() => finish(null), 220);
            }
            return list.map((i) => (i.id === item.id ? { ...i, progress: next } : i));
          });
        };
        timers.current[item.id] = setInterval(step, 220);
        return;
      }

      try {
        const { uploadUrl, uploadId, headers } = await documentService.createUploadUrl({
          fileName: item.name,
          mimeType: item.mimeType,
          sizeBytes: item.size,
          folderId: item.folderId,
          documentId: item.documentId || undefined,
        });

        // The server accepted the name, the size, the extension and the caller's right to
        // write to this folder. That all happened before a single byte was sent.
        patch(item.id, { checked: true });

        // A relative URL means the local storage driver; S3 returns an absolute one.
        const target = uploadUrl.startsWith('http') ? uploadUrl : `${window.location.origin}${uploadUrl}`;

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          timers.current[item.id] = xhr;
          xhr.open('PUT', target);
          Object.entries(headers || { 'Content-Type': item.mimeType }).forEach(([k, v]) => xhr.setRequestHeader(k, v));
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) patch(item.id, { progress: Math.round((e.loaded / e.total) * 100) });
          };
          xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
          xhr.onerror = () => reject(new Error('The connection was interrupted.'));
          xhr.onabort = () => reject(new Error('cancelled'));
          xhr.send(item.file);
        });

        await finish(uploadId);
      } catch (error) {
        if (error.message === 'cancelled') return;
        patch(item.id, { status: 'failed', error: error.message });
      }
    },
    [patch, notifyComplete, followProcessing],
  );

  /**
   * Queue files. `options.documentId` turns the upload into a new version of that
   * document; `options.changeNote` is stored against the version.
   */
  const addFiles = useCallback(
    (files, folderId, options = {}) => {
      const accepted = [];
      const rejected = [];
      Array.from(files).forEach((file) => {
        const ext = getExtension(file.name);
        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
          rejected.push({ name: file.name, reason: `.${ext || 'unknown'} files are not permitted` });
          return;
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          rejected.push({ name: file.name, reason: `exceeds the ${MAX_SIZE_MB} MB limit (${formatBytes(file.size)})` });
          return;
        }
        idRef.current += 1;
        accepted.push({
          id: `upl-${idRef.current}`,
          name: file.name,
          size: file.size,
          mimeType: file.type || 'application/octet-stream',
          folderId,
          documentId: options.documentId || null,
          changeNote: options.changeNote || '',
          file,
          progress: 0,
          status: 'queued',
          error: null,
          document: null,
        });
      });

      if (accepted.length) {
        setItems((list) => [...list, ...accepted]);
        setTrayOpen(true);
        setMinimised(false);
        accepted.forEach((item, i) => setTimeout(() => runUpload(item), i * 160));
      }
      return { accepted: accepted.length, rejected };
    },
    [runUpload],
  );

  const retry = useCallback(
    (id) => {
      const item = items.find((i) => i.id === id);
      if (item) runUpload(item);
    },
    [items, runUpload],
  );

  const cancel = useCallback(
    (id) => {
      const handle = timers.current[id];
      if (handle) {
        if (typeof handle === 'object' && handle.abort) handle.abort();
        else clearInterval(handle);
        delete timers.current[id];
      }
      patch(id, { status: 'cancelled', progress: 0 });
    },
    [patch],
  );

  const remove = useCallback((id) => setItems((list) => list.filter((i) => i.id !== id)), []);

  const clearFinished = useCallback(() => {
    setItems((list) => list.filter((i) => !['done', 'cancelled'].includes(i.status)));
  }, []);

  const closeTray = useCallback(() => {
    setTrayOpen(false);
    setItems((list) => list.filter((i) => ['uploading', 'queued', 'committing', 'processing'].includes(i.status)));
  }, []);

  const value = useMemo(() => {
    const active = items.filter((i) => ['queued', 'uploading', 'committing', 'processing'].includes(i.status));
    const failed = items.filter((i) => i.status === 'failed');
    const overall = items.length
      ? Math.round(items.reduce((sum, i) => sum + (i.status === 'done' ? 100 : i.progress), 0) / items.length)
      : 0;
    return {
      items,
      activeCount: active.length,
      failedCount: failed.length,
      overallProgress: overall,
      trayOpen: trayOpen && items.length > 0 && !suppressed,
      minimised,
      suppressTray: setSuppressed,
      setMinimised,
      addFiles,
      retry,
      cancel,
      remove,
      clearFinished,
      closeTray,
      onUploadComplete,
      maxSizeMb: MAX_SIZE_MB,
    };
  }, [items, trayOpen, minimised, suppressed, addFiles, retry, cancel, remove, clearFinished, closeTray, onUploadComplete]);

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUploads() {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUploads must be used inside <UploadProvider>');
  return ctx;
}

export default UploadContext;
